import type { CommentsProps } from './data';

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import UserComment from '../UserComment';

import {
  Box,
  Button,
  Grid,
  GridItem,
  Pagination,
  Stack,
  Text,
  Textarea,
} from '@/components';
import {
  useInputAttributes,
  useProfile,
  useSendProductComment,
  useTranslation,
} from '@/hooks';
import { yupValidator } from '@/utils';

const Comments: CommentsProps = ({
  productGroupCode,
  pagination,
  comments,
  galleryImages,
}) => {
  const { t } = useTranslation('pages.products');
  const { data: profile } = useProfile();

  const { mutateAsync: sendProductComment, isPending: pendingProductComment } =
    useSendProductComment();

  const validationSchema = yupValidator.object().shape({
    comment: yupValidator
      .string()
      .max(500)
      .label(t('registerComment'))
      .required(),
  });

  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
    resetField,
  } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const filedInputProps = useInputAttributes(control, register, errors, {
    comment:
      comments.length > 0
        ? t('commentDescription')
        : t('submitTheFirstComment'),
  });

  const sendForm = async (data: { comment: string }) => {
    await sendProductComment({
      description: data.comment,
      product_group_code: productGroupCode,
    });
    resetField('comment');
  };

  return (
    <Box>
      <Grid templateColumns={{ base: '1fr', lg: '35% 1fr' }} gap={8}>
        <GridItem
          colSpan={1}
          borderLeft={{ base: 'none', lg: '1px solid' }}
          borderBottom={{ base: '1px solid', lg: 'none' }}
          borderBottomColor={{ base: 'primary.50', lg: 'none' }}
          borderLeftColor={{ base: 'none', lg: 'primary.50' }}
          pl={{ base: 0, lg: 5 }}
          pb={{ base: 10, lg: 0 }}
          pr={{ base: 0, lg: '22px' }}
          marginInline={'10px'}
        >
          {profile ? (
            <>
              <Textarea
                {...filedInputProps.comment}
                bg="white"
                shadow="xs"
                mb={4}
              />
              <Stack direction="row" justify="flex-end">
                <Button
                  size="md"
                  isLoading={pendingProductComment}
                  onClick={(data) => void handleSubmit(sendForm)(data)}
                >
                  {t('send')}
                </Button>
              </Stack>
            </>
          ) : (
            <Text color="primary.300" fontSize={'md'}>
              {t('toSendCommentPleaseLogin')}
            </Text>
          )}
        </GridItem>
        <GridItem colSpan={1}>
          {comments.length > 0 && (
            <Stack flexDir="column" paddingInline={'10px'}>
              {comments.map((comment) => (
                <UserComment
                  key={comment.id}
                  comment={comment}
                  galleryImages={galleryImages}
                />
              ))}
            </Stack>
          )}

          {pagination.total > 10 && (
            <Box mt={{ base: '10', md: '95px' }}>
              <Pagination
                scrollToId="#comments"
                totalCount={pagination?.total}
                siblingCount={1}
                currentPage={Number(pagination?.current) || 1}
                pageSize={pagination?.page_size || 12}
              />
            </Box>
          )}
          {comments.length === 0 && (
            <Text color="primary.300" fontSize={'md'}>
              {t('noCommentsForProducts')}
            </Text>
          )}
        </GridItem>
      </Grid>
    </Box>
  );
};
export default Comments;
